import { useState } from "react";
import { supabase } from "../supabaseClient";
import { CredentiaWordmark } from "./CredentiaLogo";
import TurnstileWidget, { isTurnstileEnabled } from "./TurnstileWidget";
import { isDisposableEmail, markSent, secondsUntilNextSend } from "../emailGuard";

/**
 * Full-page gate for /cv — magic link only (no password).
 * Redirect target after clicking email link: same origin + /cv
 */
export const PENDING_OPTIN_KEY = "credentia_cv_pending_optin";
const LAST_SEND_KEY = "credentia_cv_last_magic_link";

export default function CvMagicLinkGate() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const [honeypot, setHoneypot] = useState("");

  const redirectTo = `${window.location.origin}/cv`;

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");

    // Bots fill hidden fields; pretend success so they get no signal to adapt to.
    if (honeypot) {
      setSent(true);
      return;
    }
    if (isDisposableEmail(email)) {
      setError("Please use a permanent email address (disposable email services aren't supported).");
      return;
    }
    const wait = secondsUntilNextSend(LAST_SEND_KEY);
    if (wait > 0) {
      setError(`A link was just sent. Please wait ${wait}s before requesting another.`);
      return;
    }
    if (isTurnstileEnabled && !captchaToken) {
      setError("Please complete the verification challenge.");
      return;
    }

    setLoading(true);
    const { error: err } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        emailRedirectTo: redirectTo,
        ...(captchaToken ? { captchaToken } : {}),
      },
    });
    setLoading(false);
    if (err) {
      setError(err.message);
      setCaptchaReset(n => n + 1);
      return;
    }
    markSent(LAST_SEND_KEY);
    // Persist opt-in preference so CvAnalyserPage can write it once authenticated
    try {
      localStorage.setItem(PENDING_OPTIN_KEY, JSON.stringify({
        email: email.trim().toLowerCase(),
        optIn: marketingOptIn,
        ts: new Date().toISOString(),
      }));
    } catch { /* ignore */ }
    setSent(true);
  };

  const bg = "#0a1628";
  const teal = "#2FAF9F";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Inter',system-ui,sans-serif",
        padding: "28px 18px",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=DM+Serif+Display&display=swap');
        .cv-gate-input::placeholder { color: #4a6278; }
        .cv-gate-input:focus { outline: none; border-color: ${teal} !important; box-shadow: 0 0 0 3px rgba(47,175,159,0.2); }
      `}</style>

      <div style={{ width: "100%", maxWidth: 440 }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <CredentiaWordmark iconSize={44} />
        </div>

        {!sent ? (
          <>
            <h1
              style={{
                fontFamily: "'DM Serif Display',Georgia,serif",
                fontSize: 26,
                fontWeight: 700,
                color: "#F8FAFC",
                textAlign: "center",
                lineHeight: 1.2,
                margin: "0 0 14px",
              }}
            >
              Free CESR CV Analysis
            </h1>
            <p
              style={{
                margin: "0 0 28px",
                fontSize: 15,
                color: "#94A3B8",
                lineHeight: 1.65,
                textAlign: "center",
              }}
            >
              Get an AI-powered review of your CV against GMC Portfolio Pathway guidance in under 60 seconds. Free — no credit card needed.
            </p>

            <ul style={{ margin: "0 0 32px", padding: "0 0 0 22px", color: teal, fontSize: 14, lineHeight: 1.75, fontWeight: 500 }}>
              <li style={{ marginBottom: 8 }}>13 sections reviewed against GMC criteria</li>
              <li style={{ marginBottom: 8 }}>Specific gaps identified with recommendations</li>
              <li>Instant results — no waiting</li>
            </ul>

            <form onSubmit={handleSubmit}>
              <label htmlFor="cv-gate-email" style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#CBD5E1", marginBottom: 8 }}>
                Enter your email address
              </label>
              <input
                id="cv-gate-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="cv-gate-input"
                placeholder="you@hospital.nhs.uk"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "14px 16px",
                  borderRadius: 10,
                  border: "1px solid #1e3a52",
                  background: "#0d1f33",
                  color: "#F1F5F9",
                  fontSize: 15,
                  marginBottom: 16,
                }}
              />
              <input
                type="text"
                name="company_website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={honeypot}
                onChange={e => setHoneypot(e.target.value)}
                style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
              />
              {error ? (
                <p style={{ color: "#fca5a5", fontSize: 13, margin: "0 0 14px" }}>{error}</p>
              ) : null}

              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  marginBottom: 16,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={marketingOptIn}
                  onChange={e => setMarketingOptIn(e.target.checked)}
                  style={{ marginTop: 2, accentColor: teal, flexShrink: 0, width: 15, height: 15, cursor: "pointer" }}
                />
                <span style={{ fontSize: 12.5, color: "#94A3B8", lineHeight: 1.6 }}>
                  I'd like to receive product updates about Credentia by email.
                </span>
              </label>

              <div style={{ marginBottom: isTurnstileEnabled ? 16 : 0 }}>
                <TurnstileWidget
                  onToken={setCaptchaToken}
                  onError={() => setError("Verification failed to load. Please refresh and try again.")}
                  resetSignal={captchaReset}
                />
              </div>

              <button
                type="submit"
                disabled={loading || (isTurnstileEnabled && !captchaToken)}
                style={{
                  width: "100%",
                  padding: "14px 20px",
                  borderRadius: 10,
                  border: "none",
                  background: teal,
                  color: "#fff",
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: loading ? "wait" : "pointer",
                  opacity: loading || (isTurnstileEnabled && !captchaToken) ? 0.6 : 1,
                  fontFamily: "inherit",
                }}
              >
                {loading ? "Sending…" : "Send me a magic link"}
              </button>
            </form>

            <p style={{ marginTop: 22, fontSize: 12, color: "#64748B", lineHeight: 1.6, textAlign: "center" }}>
              We&apos;ll email you a one-click login link. No password needed. No spam.
            </p>
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "8px 0 0" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }} aria-hidden>
              ✉️
            </div>
            <h2 style={{ fontFamily: "'DM Serif Display',Georgia,serif", fontSize: 22, fontWeight: 700, color: "#F8FAFC", margin: "0 0 12px" }}>
              Check your email
            </h2>
            <p style={{ fontSize: 15, color: "#94A3B8", lineHeight: 1.65, margin: "0 0 8px" }}>
              We&apos;ve sent a link to <strong style={{ color: "#E2E8F0" }}>{email}</strong>. Click it to access your free CV analysis.
            </p>
            <p style={{ fontSize: 14, color: "#64748B", lineHeight: 1.6, margin: 0 }}>You can close this tab.</p>
          </div>
        )}
      </div>
    </div>
  );
}
